import { isEmptyObject } from "@entrilia/shared/utilities/objectHelper.ts";
import type { FieldConfiguration } from "../../../../../api/types/fieldConfigurationTypes.ts";
import { FieldConfigurationType } from "../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ChartConfiguration,
  FinancialStatementConfiguration,
  PivotConfiguration,
  ReportConfiguration,
  ReportType,
  StatementLineConfiguration,
  TabularConfiguration,
  TileConfiguration,
} from "../../../../../api/types/svTypes.ts";
import { chartRelatedReportTypes } from "./chartRelatedReport.types.ts";
import {
  isFinancialStatementReportType,
  isListReportType,
  isPivotReportType,
  isTileReportType,
  usesTabularConfiguration,
} from "./reportTypeHelper.ts";
import { unique } from "./unique.ts";

export const getMeasureNames = (reportType: ReportType | undefined, configuration: ReportConfiguration | undefined) => {
  if (configuration === undefined || reportType === undefined) {
    return [];
  }
  if (isPivotReportType(reportType)) {
    const { values } = configuration as PivotConfiguration;
    return getPivotMeasureNames(values);
  }
  if (usesTabularConfiguration(reportType)) {
    const { fields } = configuration as TabularConfiguration;
    return getTabularMeasureNames(fields);
  }
  if (chartRelatedReportTypes.includes(reportType)) {
    const { values } = configuration as ChartConfiguration;
    return getChartMeasureNames(values);
  }
  if (isTileReportType(reportType)) {
    const { fields } = configuration as TileConfiguration;
    return getTileMeasureNames(fields);
  }
  if (isListReportType(reportType)) {
    return [];
  }
  if (isFinancialStatementReportType(reportType)) {
    const { lines } = configuration as FinancialStatementConfiguration;
    return getFinancialStatementMeasureNames(lines);
  }
  throw new Error("Report type not supported");
};

export const getTileMeasureNames = (fields: FieldConfiguration[]) => {
  return getTabularMeasureNames(fields);
};

export const getPivotMeasureNames = (values: FieldConfiguration[]) => {
  if (isEmptyObject(values)) {
    return [];
  }
  return values.map((c) => c.name).sort();
};

export const getTabularMeasureNames = (fields: FieldConfiguration[]) => {
  if (isEmptyObject(fields)) {
    return [];
  }
  return fields
    .filter((f) => f.type === FieldConfigurationType.Measure)
    .map((f) => f.name)
    .filter((f): f is string => !!f)
    .sort();
};

export const getChartMeasureNames = (values: FieldConfiguration[]) => {
  if (isEmptyObject(values)) {
    return [];
  }
  return values.map((c) => c.name).sort();
};

export const getFinancialStatementMeasureNames = (lines: StatementLineConfiguration[]): string[] =>
  lines
    .flatMap((line) => (line.field ? [line.field.name] : []))
    .filter(unique)
    .sort();
